import { useTeam } from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { bankersRound, formatCurrency, toTitleCase } from '@vrplatform/utils';
import { Text } from '@mantine/core';
import { Group, HoverCard, Stack } from '@mantine/core';
import type { taxBehavior_enum } from '@vrplatform/graphql';
import { formatPercentage } from '@vrplatform/ui-common';
import { useMemo } from 'react';
import { useTaxRatesQuery } from '@vrplatform/data';

export const DepositLineAmountHoverCard = ({
  currency: currencyProp,
  ...data
}: {
  paymentCentTotal: number;
  reserveCentTotal?: number;
  merchantFeeCentTotal?: number;
  vatCentTotal?: number;
  channelFeeCentTotal?: number;
  rateId?: string | null;
  rateBehavior?: taxBehavior_enum;
  coHostCentTotal?: number;
  currency?: string;
}) => {
  const [{ currency: teamCurrency }] = useTeam();
  const currency = currencyProp || teamCurrency;
  const { data: rates, isLoading: loadingRates } = useTaxRatesQuery();

  const rate = data.rateId
    ? rates?.find((x) => x.value === data.rateId)
    : undefined;

  const total = useMemo(() => {
    const p = Number(data.paymentCentTotal || 0);

    const appliedAmount =
      rate && data.rateBehavior === 'excluded'
        ? bankersRound(p + p * (rate.percentageSuperBasePoints / 100000))
        : p;

    return (
      appliedAmount +
      Number(data.merchantFeeCentTotal || 0) +
      Number(data.channelFeeCentTotal || 0) +
      Number(data.reserveCentTotal || 0) +
      Number(data.vatCentTotal || 0) +
      Number(data.coHostCentTotal || 0)
    );
  }, [
    data.paymentCentTotal,
    data.channelFeeCentTotal,
    data.merchantFeeCentTotal,
    data.rateBehavior,
    data.reserveCentTotal,
    data.vatCentTotal,
    data.coHostCentTotal,
    rate,
  ]);

  return (
    <HoverCard
      position="bottom-end"
      withArrow
      withinPortal
      width={250}
      shadow="md"
      radius="md"
      arrowOffset={10}
    >
      <HoverCard.Target>
        <Text>{formatCurrency(total / 100, currency)}</Text>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap="xs">
          <Group wrap="nowrap" align="flex-start">
            <Text c="gray" flex={1}>
              Amount applied
            </Text>
            <Text ta="right" fw={500}>
              {formatCurrency(data.paymentCentTotal / 100, currency)}
            </Text>
          </Group>
          {'rateId' in data && !!data.rateId && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Tax Rate{' '}
                <Text span size="xs" c="gray">
                  ({toTitleCase(data.rateBehavior)})
                </Text>
              </Text>
              {loadingRates ? (
                <LoadingIndicator size="xs" />
              ) : (
                <Text ta="right" fw={500}>
                  {formatPercentage(
                    rates?.find((x) => x.value === data.rateId)
                      ?.percentageSuperBasePoints || 0,
                    'super-base-points'
                  )}
                </Text>
              )}
            </Group>
          )}
          {'merchantFeeCentTotal' in data && !!data.merchantFeeCentTotal && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Merchant fee
              </Text>
              <Text ta="right" fw={500}>
                {formatCurrency(
                  (data.merchantFeeCentTotal ?? 0) / 100,
                  currency
                )}
              </Text>
            </Group>
          )}
          {'channelFeeCentTotal' in data && !!data.channelFeeCentTotal && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Channel fee
              </Text>
              <Text ta="right" fw={500}>
                {formatCurrency(
                  (data.channelFeeCentTotal ?? 0) / 100,
                  currency
                )}
              </Text>
            </Group>
          )}
          {'reserveCentTotal' in data && !!data.reserveCentTotal && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Reserve
              </Text>
              <Text ta="right" fw={500}>
                {formatCurrency((data.reserveCentTotal ?? 0) / 100, currency)}
              </Text>
            </Group>
          )}
          {'coHostCentTotal' in data && !!data.coHostCentTotal && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Co-Host
              </Text>
              <Text ta="right" fw={500}>
                {formatCurrency((data.coHostCentTotal ?? 0) / 100, currency)}
              </Text>
            </Group>
          )}
          {'vatCentTotal' in data && !!data.vatCentTotal && (
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                VAT
              </Text>
              <Text ta="right" fw={500}>
                {formatCurrency((data.vatCentTotal ?? 0) / 100, currency)}
              </Text>
            </Group>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
